// ============================================================================
// CLEO'S SECRET — PrepModule
// File: src/pages/PrepModule.jsx
//
// The individual curriculum module reader. Opens from PrepCurriculum when a
// patient taps a module. Four blocks stacked vertically:
//   • Editorial header — roman numeral + day/duration meta + title + subtitle
//   • Video block (or "video coming soon" placeholder on OBS)
//   • The science — markdown explainer with h2 / p / ul rendering
//   • For you to consider — italic prompt + reflection textarea + save
//   • Prev / next module navigation
//
// Pattern: PATIENT-FACING EDITORIAL with reading-width body.
//
// Already on the May 2026 design system; this pass:
//   • Replaces inline Cinzel font strings with CINZEL constant
//   • Explainer body switched from FS (sans) to FF (serif) per Stage 2
//   • Italic reflection prompt: weight 300 → 400 (Stage 2 minimum)
//   • Removes ✓ glyph + ← arrow in prev/next nav
//   • Saved state uses palette signal (GREEN) instead of checkmark
//   • Video block left bar 6px → 3px (matches platform standard)
//   • Section heads use a single SectionLabel atom signature
//
// API endpoints preserved:
//   • /api/get-prep-module      (loads module + curriculum context)
//   • /api/save-reflection      (saves patient reflection)
// ============================================================================

import React, { useState, useEffect } from 'react';
import {
  P, FF, FS,
  AnkhMed, GeoDivider, BtnPrimary, BtnGhost, Spinner,
} from '../lib/design-tokens';

// ───────── Helpers ─────────
function romanNumeral(n) {
  var map = ['I','II','III','IV','V','VI','VII','VIII','IX','X','XI','XII'];
  return map[n - 1] || String(n);
}

function formatSecondsAsMin(sec) {
  if (!sec) return '4-6 min';
  return Math.round(sec / 60) + ' min';
}

// ═════════════════════════════════════════════════════════════════════════
// PrepModule
// ═════════════════════════════════════════════════════════════════════════
export default function PrepModule(props) {
  var onNavigate = props.onNavigate;
  var email = props.email;
  var moduleSequenceNumber = props.moduleSequenceNumber;

  var dataState = useState(null);
  var data = dataState[0];
  var setData = dataState[1];

  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];

  var errorState = useState(null);
  var error = errorState[0];
  var setError = errorState[1];

  var reflectionState = useState('');
  var reflection = reflectionState[0];
  var setReflection = reflectionState[1];

  var savingState = useState(false);
  var saving = savingState[0];
  var setSaving = savingState[1];

  var savedAtState = useState(null);
  var savedAt = savedAtState[0];
  var setSavedAt = savedAtState[1];

  var allModulesState = useState([]);
  var allModules = allModulesState[0];
  var setAllModules = allModulesState[1];

  var emailWaitState = useState(0);
  var emailWaitMs = emailWaitState[0];
  var setEmailWaitMs = emailWaitState[1];

  useEffect(function() {
    if (!moduleSequenceNumber) {
      setError('Missing module reference');
      setLoading(false);
      return;
    }
    if (!email) {
      if (emailWaitMs >= 3000) {
        setError('You need to be logged in to view this module.');
        setLoading(false);
        return;
      }
      var timer = setTimeout(function() { setEmailWaitMs(emailWaitMs + 500); }, 500);
      return function() { clearTimeout(timer); };
    }

    fetch('/api/get-prep-module', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email: email, moduleSequenceNumber: moduleSequenceNumber }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) {
          throw new Error(e.error || 'Could not load module');
        });
        return res.json();
      })
      .then(function(result) {
        if (!result.selectedModule) throw new Error('Module is not available');
        setData(result.selectedModule);
        setAllModules(result.modules || []);
        if (result.selectedModule.existingReflection) {
          setReflection(result.selectedModule.existingReflection);
          setSavedAt('previously');
        }
        setLoading(false);
      })
      .catch(function(err) {
        setError(err.message);
        setLoading(false);
      });
  }, [email, moduleSequenceNumber, emailWaitMs]);

  function saveReflection() {
    if (!reflection.trim()) return;
    setSaving(true);
    setSavedAt(null);
    fetch('/api/save-reflection', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        email: email,
        moduleSequenceNumber: moduleSequenceNumber,
        reflectionText: reflection,
      }),
    })
      .then(function(res) {
        if (!res.ok) return res.json().then(function(e) { throw new Error(e.error || 'Could not save'); });
        return res.json();
      })
      .then(function(result) {
        setSavedAt(result.savedAt);
        setSaving(false);
      })
      .catch(function(err) {
        setError(err.message);
        setSaving(false);
      });
  }

  function goToNextModule() {
    if (!data) return;
    var nextSeq = data.sequenceNumber + 1;
    var nextModule = allModules.find(function(m) { return m.sequenceNumber === nextSeq; });
    if (nextModule && nextModule.isUnlocked) {
      if (onNavigate) onNavigate('prep-module', { moduleSequenceNumber: nextSeq });
    } else {
      if (onNavigate) onNavigate('prep-curriculum');
    }
  }

  // ────────────────────────────────────────────────────────────────────────
  // LOADING STATE
  // ────────────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <Spinner color={P.GO} size={48} />
      </div>
    );
  }

  // ────────────────────────────────────────────────────────────────────────
  // ERROR STATE
  // ────────────────────────────────────────────────────────────────────────
  if (error || !data) {
    return (
      <div style={{
        background: P.PAP,
        minHeight: '100vh',
        padding: 'clamp(80px, 12vw, 140px) clamp(24px, 3vw, 40px)',
        textAlign: 'center',
        fontFamily: FF,
      }}>
        <div style={{ maxWidth: 600, margin: '0 auto' }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(11px, 0.95vw, 13px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
            marginBottom: 22,
          }}>Could not load</div>

          <h1 style={{
            fontFamily: FF,
            fontSize: 'clamp(34px, 4.2vw, 52px)',
            fontWeight: 400,
            color: P.INK,
            lineHeight: 1.1,
            letterSpacing: '-0.014em',
            margin: '0 0 clamp(22px, 2.4vw, 32px)',
          }}>
            Something didn't <span style={{ fontStyle: 'italic', color: P.BR2 }}>load.</span>
          </h1>

          <div style={{
            width: 'clamp(120px, 12vw, 160px)',
            height: 1,
            background: P.GO,
            margin: '0 auto clamp(28px, 3vw, 36px)',
            opacity: 0.85,
          }} />

          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(17px, 1.6vw, 20px)',
            color: P.TL,
            lineHeight: 1.75,
            maxWidth: 500,
            margin: '0 auto clamp(36px, 4vw, 48px)',
          }}>{error || 'Unknown error.'}</p>

          <BtnGhost onClick={function() { if (onNavigate) onNavigate('prep-curriculum'); }}>
            Back to curriculum
          </BtnGhost>
        </div>
      </div>
    );
  }

  var nextModule = allModules.find(function(m) { return m.sequenceNumber === data.sequenceNumber + 1; });
  var prevModule = allModules.find(function(m) { return m.sequenceNumber === data.sequenceNumber - 1; });

  // ════════════════════════════════════════════════════════════════════════
  // MAIN RENDER
  // ════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ background: P.PAP, minHeight: '100vh', fontFamily: FF }}>

      {/* ═════════ Editorial header ═════════ */}
      <header style={{
        background: P.SAND,
        borderBottom: '1px solid ' + P.SAND3,
        padding: 'clamp(72px, 9vw, 112px) clamp(24px, 3vw, 40px) clamp(48px, 6vw, 72px)',
        textAlign: 'center',
      }}>
        {/* Roman numeral + day/duration stack */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 18,
          marginBottom: 'clamp(32px, 3.6vw, 44px)',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(50px, 6.8vw, 80px)',
            fontWeight: 400,
            color: P.BR,
            lineHeight: 0.9,
            letterSpacing: '-0.02em',
          }}>{romanNumeral(data.sequenceNumber)}</div>

          <div style={{ width: 48, height: 1, background: P.GO, opacity: 0.85 }} />

          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.2vw, 16px)',
            fontWeight: 700,
            letterSpacing: '0.34em',
            textTransform: 'uppercase',
            color: P.BR2,
          }}>Day {data.unlockDay} &middot; {formatSecondsAsMin(data.videoDurationSeconds)}</div>
        </div>

        {/* Title */}
        <h1 style={{
          fontFamily: FF,
          fontSize: 'clamp(40px, 5.4vw, 68px)',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.08,
          letterSpacing: '-0.014em',
          margin: '0 0 ' + (data.subtitle ? 'clamp(20px, 2.4vw, 28px)' : '0'),
          maxWidth: 920,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>{data.title}</h1>

        {/* Subtitle */}
        {data.subtitle && (
          <p style={{
            fontFamily: FF,
            fontSize: 'clamp(20px, 2.4vw, 30px)',
            fontStyle: 'italic',
            fontWeight: 400,
            color: P.BR2,
            lineHeight: 1.4,
            letterSpacing: '-0.005em',
            maxWidth: 760,
            margin: '0 auto',
          }}>{data.subtitle}</p>
        )}
      </header>

      {/* ═════════ Body ═════════ */}
      <main style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'clamp(56px, 7vw, 96px) clamp(24px, 3vw, 40px) clamp(64px, 8vw, 112px)',
      }}>

        {/* Video block */}
        <VideoBlock videoUrl={data.videoUrl} />

        <GeoDivider marginV={64} />

        {/* The science */}
        <SectionLabel>The science</SectionLabel>
        <div style={{ marginTop: 'clamp(36px, 4vw, 52px)' }}>
          <ExplainerMarkdown markdown={data.explainerMarkdown} />
        </div>

        <GeoDivider marginV={64} />

        {/* For you to consider */}
        <SectionLabel>For you to consider</SectionLabel>

        {/* Reflection prompt as italic blockquote */}
        <blockquote style={{
          fontFamily: FF,
          fontSize: 'clamp(22px, 2.6vw, 32px)',
          fontStyle: 'italic',
          fontWeight: 400,
          color: P.INK,
          lineHeight: 1.4,
          letterSpacing: '-0.005em',
          margin: 'clamp(36px, 4vw, 52px) 0 clamp(28px, 3vw, 40px)',
          paddingLeft: 28,
          borderLeft: '3px solid ' + P.GO,
        }}>{data.reflectionPrompt}</blockquote>

        {/* Reflection textarea */}
        <textarea
          value={reflection}
          onChange={function(e) { setReflection(e.target.value); setSavedAt(null); }}
          placeholder="Take your time. Anything you write here is only visible to you and your GP."
          rows={6}
          style={{
            width: '100%',
            padding: '20px 22px',
            fontFamily: FF,
            fontSize: 'clamp(15px, 1.4vw, 17px)',
            color: P.INK,
            background: P.PAP,
            border: '1px solid ' + P.SAND3,
            outline: 'none',
            resize: 'vertical',
            boxSizing: 'border-box',
            lineHeight: 1.75,
            transition: 'border-color 0.2s',
          }}
          onFocus={function(e) { e.currentTarget.style.borderColor = P.BR; }}
          onBlur={function(e) { e.currentTarget.style.borderColor = P.SAND3; }}
        />

        {/* Save status + button */}
        <div style={{
          marginTop: 20,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 14,
          flexWrap: 'wrap',
        }}>
          <div style={{
            fontFamily: FF,
            fontSize: 'clamp(13px, 1.1vw, 14px)',
            color: P.TF,
            fontStyle: 'italic',
            lineHeight: 1.5,
          }}>
            {saving && 'Saving\u2026'}
            {savedAt === 'previously' && !saving && 'You wrote this earlier. Edit and save to update.'}
            {savedAt && savedAt !== 'previously' && !saving && (
              <span style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.GREEN,
                background: P.GREEN_BG,
                border: '1px solid ' + P.GREEN,
                padding: '4px 12px',
                fontStyle: 'normal',
              }}>Saved</span>
            )}
            {!savedAt && !saving && reflection.length > 0 && (
              <span style={{
                fontFamily: FF,
                fontSize: 'clamp(11px, 0.95vw, 13px)',
                fontWeight: 700,
                letterSpacing: '0.34em',
                textTransform: 'uppercase',
                color: P.BR2,
                fontStyle: 'normal',
              }}>Unsaved changes</span>
            )}
          </div>
          <BtnGhost
            onClick={saveReflection}
            disabled={!reflection.trim() || saving}>
            {saving ? 'Saving' : 'Save reflection'}
          </BtnGhost>
        </div>

        {/* Navigation */}
        <div style={{
          marginTop: 'clamp(64px, 8vw, 96px)',
          paddingTop: 'clamp(36px, 4vw, 52px)',
          borderTop: '1px solid ' + P.SAND3,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 16,
          flexWrap: 'wrap',
        }}>
          {/* Prev link */}
          <div style={{ flex: 1, minWidth: 220 }}>
            {prevModule && (
              <button
                onClick={function() { if (onNavigate) onNavigate('prep-module', { moduleSequenceNumber: prevModule.sequenceNumber }); }}
                style={{
                  fontFamily: FS,
                  fontSize: 'clamp(10px, 0.85vw, 12px)',
                  fontWeight: 600,
                  letterSpacing: '0.28em',
                  textTransform: 'uppercase',
                  color: P.BR2,
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: 0,
                  textAlign: 'left',
                  transition: 'color 0.2s',
                }}
                onMouseEnter={function(e) { e.currentTarget.style.color = P.BR; }}
                onMouseLeave={function(e) { e.currentTarget.style.color = P.BR2; }}>
                &larr; {prevModule.title}
              </button>
            )}
          </div>

          {/* Primary next button */}
          <BtnPrimary onClick={goToNextModule}>
            {nextModule && nextModule.isUnlocked ? 'Next module' : 'Back to curriculum'}
          </BtnPrimary>

          {/* Next preview */}
          <div style={{ flex: 1, minWidth: 220, textAlign: 'right' }}>
            {nextModule && !nextModule.isUnlocked && (
              <div style={{
                fontFamily: FF,
                fontSize: 'clamp(13px, 1.1vw, 14px)',
                fontStyle: 'italic',
                color: P.TF,
                lineHeight: 1.5,
              }}>
                Next: {nextModule.title} (Day {nextModule.unlockDay})
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// SectionLabel — short rule + Cormorant 700 label + flexible rule
// ═════════════════════════════════════════════════════════════════════════
function SectionLabel({ children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
      <span style={{
        width: 32,
        height: 1,
        background: P.GO,
        opacity: 0.85,
      }} />
      <span style={{
        fontFamily: FF,
        fontSize: 'clamp(11px, 0.95vw, 13px)',
        fontWeight: 700,
        letterSpacing: '0.34em',
        textTransform: 'uppercase',
        color: P.BR2,
        whiteSpace: 'nowrap',
      }}>{children}</span>
      <span style={{ flex: 1, height: 1, background: P.SAND3 }} />
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// VideoBlock — actual video, or "coming soon" placeholder on OBS
// ═════════════════════════════════════════════════════════════════════════
function VideoBlock({ videoUrl }) {
  if (!videoUrl) {
    return (
      <div style={{
        background: P.OBS,
        borderLeft: '3px solid ' + P.GO,
        padding: 'clamp(64px, 8vw, 112px) clamp(28px, 4vw, 48px)',
        textAlign: 'center',
      }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <AnkhMed color={P.GO2} opacity={0.85} />
        </div>
        <div style={{
          fontFamily: FF,
          fontSize: 'clamp(11px, 0.95vw, 13px)',
          fontWeight: 700,
          letterSpacing: '0.34em',
          textTransform: 'uppercase',
          color: P.GO2,
          marginTop: 'clamp(28px, 3vw, 40px)',
          marginBottom: 22,
        }}>Video coming soon</div>
        <p style={{
          fontFamily: FF,
          fontSize: 'clamp(20px, 2.4vw, 26px)',
          fontStyle: 'italic',
          fontWeight: 400,
          color: 'rgba(240,232,212,0.92)',
          lineHeight: 1.5,
          maxWidth: 540,
          margin: '0 auto',
        }}>Your GP is recording this module. The text below tells you everything you need to know.</p>
      </div>
    );
  }

  return (
    <div style={{
      position: 'relative',
      paddingTop: '56.25%',
      background: P.OBS,
      border: '1px solid ' + P.SAND3,
    }}>
      <video
        controls
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
        }}>
        <source src={videoUrl} type="video/mp4" />
      </video>
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════
// ExplainerMarkdown — renders h2 / p / ul with Stage 2 typography
// (FF Cormorant body, justified, diamond bullets)
// ═════════════════════════════════════════════════════════════════════════
function ExplainerMarkdown({ markdown }) {
  if (!markdown) return null;
  var blocks = parseBlocks(markdown);

  return (
    <div>
      {blocks.map(function(block, i) {
        if (block.type === 'h2') {
          return (
            <h2 key={i} style={{
              fontFamily: FF,
              fontSize: 'clamp(24px, 2.8vw, 32px)',
              fontStyle: 'italic',
              fontWeight: 400,
              color: P.INK,
              lineHeight: 1.25,
              letterSpacing: '-0.008em',
              marginTop: i === 0 ? 0 : 'clamp(48px, 5.6vw, 72px)',
              marginBottom: 'clamp(22px, 2.4vw, 30px)',
              textAlign: 'left',
            }}>{block.text}</h2>
          );
        }

        if (block.type === 'ul') {
          return (
            <ul key={i} style={{
              paddingLeft: 0,
              listStyle: 'none',
              margin: '8px 0 clamp(28px, 3vw, 40px)',
            }}>
              {block.items.map(function(item, j) {
                return (
                  <li key={j} style={{
                    position: 'relative',
                    paddingLeft: 36,
                    marginBottom: j === block.items.length - 1 ? 0 : 22,
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: 0,
                      top: 14,
                      width: 9,
                      height: 9,
                      background: P.BR,
                      transform: 'rotate(45deg)',
                    }} />
                    <span style={{
                      display: 'block',
                      fontFamily: FF,
                      fontSize: 'clamp(16px, 1.7vw, 19px)',
                      color: P.TX,
                      lineHeight: 1.85,
                      textAlign: 'justify',
                      WebkitHyphens: 'auto',
                      msHyphens: 'auto',
                      hyphens: 'auto',
                    }}>{renderInline(item)}</span>
                  </li>
                );
              })}
            </ul>
          );
        }

        return (
          <p key={i} style={{
            fontFamily: FF,
            fontSize: 'clamp(16px, 1.8vw, 20px)',
            color: P.TX,
            lineHeight: 1.85,
            margin: '0 0 clamp(22px, 2.4vw, 30px)',
            textAlign: 'justify',
            WebkitHyphens: 'auto',
            msHyphens: 'auto',
            hyphens: 'auto',
          }}>{renderInline(block.text)}</p>
        );
      })}
    </div>
  );
}

// ───────── Markdown block parser (h2 / p / ul) ─────────
function parseBlocks(md) {
  var lines = md.split('\n');
  var blocks = [];
  var currentPara = [];
  var currentList = null;

  function flushPara() {
    if (currentPara.length > 0) {
      blocks.push({ type: 'p', text: currentPara.join(' ').trim() });
      currentPara = [];
    }
  }
  function flushList() {
    if (currentList) {
      blocks.push({ type: 'ul', items: currentList });
      currentList = null;
    }
  }

  for (var i = 0; i < lines.length; i++) {
    var trimmed = lines[i].trim();
    if (trimmed === '') { flushPara(); flushList(); continue; }
    if (trimmed.indexOf('## ') === 0) {
      flushPara(); flushList();
      blocks.push({ type: 'h2', text: trimmed.slice(3).trim() });
      continue;
    }
    if (trimmed.indexOf('- ') === 0) {
      flushPara();
      if (!currentList) currentList = [];
      currentList.push(trimmed.slice(2).trim());
      continue;
    }
    flushList();
    currentPara.push(trimmed);
  }
  flushPara();
  flushList();
  return blocks;
}

// ───────── Inline formatting: **bold** + *italic* ─────────
function renderInline(text) {
  var nodes = [];
  var i = 0;
  var current = '';
  while (i < text.length) {
    if (text.charAt(i) === '*' && text.charAt(i + 1) === '*') {
      var end = text.indexOf('**', i + 2);
      if (end !== -1) {
        if (current) { nodes.push(current); current = ''; }
        nodes.push(
          <strong key={'b' + i} style={{ color: P.INK, fontWeight: 700 }}>
            {text.slice(i + 2, end)}
          </strong>
        );
        i = end + 2;
        continue;
      }
    }
    if (text.charAt(i) === '*' && text.charAt(i + 1) !== '*') {
      var endIt = text.indexOf('*', i + 1);
      if (endIt !== -1) {
        if (current) { nodes.push(current); current = ''; }
        nodes.push(
          <em key={'i' + i} style={{
            color: P.BR,
            fontFamily: FF,
            fontStyle: 'italic',
            fontSize: '1.05em',
          }}>{text.slice(i + 1, endIt)}</em>
        );
        i = endIt + 1;
        continue;
      }
    }
    current += text.charAt(i);
    i++;
  }
  if (current) nodes.push(current);
  return nodes;
}
